import { useEffect, useRef, useState, useCallback } from "react";

export const useUnsavedChanges = ({
  hasUnsavedChanges,
  message = "You have unsaved changes. Are you sure you want to leave?",
}) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const isNavigationConfirmed = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !isNavigationConfirmed.current) {
        e.preventDefault();

        setShowModal(true);
        setPendingNavigation(() => () => {
          isNavigationConfirmed.current = true;
          window.location.reload();
        });

        e.returnValue = "";
        return "";
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedChanges && !isNavigationConfirmed.current) {
        e.preventDefault();
        window.history.pushState(null, "", window.location.href);
        setShowModal(true);
        setPendingNavigation(() => () => {
          isNavigationConfirmed.current = true;
          window.history.back();
        });
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("popstate", handlePopState);
      window.history.pushState(null, "", window.location.href);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges]);

  const confirmNavigation = useCallback(() => {
    setShowModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
    isNavigationConfirmed.current = false;
  }, [pendingNavigation]);

  const cancelNavigation = useCallback(() => {
    setShowModal(false);
    setPendingNavigation(null);
    isNavigationConfirmed.current = false;
  }, []);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      setShowModal(false);
      setPendingNavigation(null);
      isNavigationConfirmed.current = false;
    }
  }, [hasUnsavedChanges]);

  return {
    showModal,
    confirmNavigation,
    cancelNavigation,
    message,
  };
};
